import { type ForwardRefExoticComponent, forwardRef } from 'react';

import { ToggleCardBody } from './toggle-card-body';
import { ToggleCardFooter } from './toggle-card-footer';
import { ToggleCardHeader } from './toggle-card-header';
import { ToggleCardRoot } from './toggle-card-root';
import type { ToggleCardPropsInterface } from './types/toggle-card.types';

/**
 * Toggle cards are an official
 * [WAI-ARIA button](https://www.w3.org/TR/wai-aria-practices/#button) type that use the
 * [aria-pressed](https://www.w3.org/TR/wai-aria-1.1/#aria-pressed) attribute to tell assistive
 * technologies that the card can be toggled like a button.
 *
 * **Important:** it is critical that content of the card
 * does not change when its state changes.
 *
 * @since 8.34.0
 */
export const ToggleCard = forwardRef<HTMLDivElement, ToggleCardPropsInterface>(
  (props: ToggleCardPropsInterface, forwardedRef) => {
    const { body, children, dividers = true, footer, header, ...rest } = props;

    return (
      <ToggleCard.Root ref={forwardedRef} {...rest}>
        {header ? <ToggleCard.Header divider={dividers}>{header}</ToggleCard.Header> : null}
        {body ? <ToggleCard.Body>{body}</ToggleCard.Body> : null}
        {children}
        {footer ? <ToggleCard.Footer divider={dividers}>{footer}</ToggleCard.Footer> : null}
      </ToggleCard.Root>
    );
  },
) as ForwardRefExoticComponent<ToggleCardPropsInterface> & {
  Root: typeof ToggleCardRoot;
  Header: typeof ToggleCardHeader;
  Body: typeof ToggleCardBody;
  Footer: typeof ToggleCardFooter;
};

ToggleCard.displayName = 'ToggleCard';

ToggleCard.Root = ToggleCardRoot;
ToggleCard.Header = ToggleCardHeader;
ToggleCard.Body = ToggleCardBody;
ToggleCard.Footer = ToggleCardFooter;
